import React, { useMemo, useEffect, useLayoutEffect } from "react";
import { useSelector } from "react-redux";
import { useRef } from "react";
import "./MasterSkillsSection.css";
import {
	Box,
	Typography,
	IconButton,
} from "@mui/material";
import CourseCard from "./LearningCard";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import trandingCourses from "./TrandingCourses.json";

function MasterSkillsSection() {
	const {
		inProgressCourses,
		completedCourses,
		yetToStartCourses
	} = useSelector((state) => state.courseDetails);

	const trendingCourses = useMemo(() => {
		const userCourses = [
			...(completedCourses || []),
			...(inProgressCourses || []),
			...(yetToStartCourses || []),
		];

		const userCourseNames = new Set(
			trandingCourses.map((course) => course.courseName?.trim())
		);

		return userCourses.filter((course) =>
			userCourseNames.has(course.courseName?.trim())
		);
	}, [completedCourses, inProgressCourses, yetToStartCourses]);

	const scrollRef = useRef(null);

	const scroll = (direction) => {
		if (!scrollRef.current) return;

		const scrollAmount = 320; // one card width + gap

		scrollRef.current.scrollBy({
			left: direction === "left" ? -scrollAmount : scrollAmount,
			behavior: "smooth",
		});
	};

	const cardRefs = useRef([]);
	const totalCards = trendingCourses.length;
	const [currentIndex, setCurrentIndex] = React.useState(0);
	const [maxHeight, setMaxHeight] = React.useState(0);

	useEffect(() => {
		cardRefs.current = [];
	}, [trendingCourses]);

	useLayoutEffect(() => {
		if (!cardRefs.current.length) return;

		const calculateHeight = () => {
			const heights = cardRefs.current.map(el =>
				el ? el.getBoundingClientRect().height : 0
			);

			const tallest = Math.max(...heights);
			setMaxHeight(tallest);
		};

		// Wait a bit for images to load & layout to settle
		const timer = setTimeout(calculateHeight, 150);

		return () => clearTimeout(timer);
	}, [trendingCourses]);

	const scrollToIndex = (index) => {
		const node = cardRefs.current[index];
		if (!node) return;

		node.scrollIntoView({
			behavior: "smooth",
			inline: "start",
			block: "nearest",
		});
	};

	const courseImg = [
		{ img: "/Assets/One.jpg" },
		{ img: "/Assets/Two.jpg" },
		{ img: "/Assets/Three.jpg" },
		{ img: "/Assets/Four.jpg" },
		{ img: "/Assets/Five.jpg" },
		{ img: "/Assets/One.jpg" },
		{ img: "/Assets/Two.jpg" },
		{ img: "/Assets/Three.jpg" },
		{ img: "/Assets/Four.jpg" },
	];

	const getRandomImageForCourse = (courseId) => {
		const index = courseId % courseImg.length;
		return courseImg[index];
	};

	const scrollAmount = () => {
		const card = cardRefs.current[0];
		return card ? card.offsetWidth + 16 : 300;
	};

	const updateIndexFromScroll = () => {
		if (!scrollRef.current) return;

		const containerLeft = scrollRef.current.scrollLeft;
		let closestIndex = 0;
		let minDiff = Infinity;

		cardRefs.current.forEach((card, index) => {
			if (!card) return;
			const diff = Math.abs(card?.offsetLeft - containerLeft);
			if (diff < minDiff) {
				minDiff = diff;
				closestIndex = index;
			}
		});

		setCurrentIndex(closestIndex);
	};

	const isAtEnd = () => {
		const el = scrollRef.current;
		if (!el) return false;

		return el.scrollLeft + el.clientWidth >= el.scrollWidth - 5;
	};

	const isAtStart = () => {
		const el = scrollRef.current;
		if (!el) return false;

		return el.scrollLeft <= 5;
	};

	const handleNext = () => {
		if (!scrollRef.current) return;

		if (isAtEnd()) {
			scrollRef.current.scrollTo({
				left: cardRefs?.current[0]?.offsetLeft,
				behavior: "smooth",
			});
			return;
		}

		scrollRef.current.scrollBy({
			left: scrollAmount(),
			behavior: "smooth",
		});
	};

	const handlePrev = () => {
		if (!scrollRef.current) return;

		if (isAtStart()) {
			const last = totalCards - 1;
			scrollRef.current.scrollTo({
				left: cardRefs?.current[last]?.offsetLeft,
				behavior: "smooth",
			});
			return;
		}

		scrollRef.current.scrollBy({
			left: -scrollAmount(),
			behavior: "smooth",
		});
	};

	return (
		<>
			<Box
				sx={{
					px: { xs: 2, md: 6 },
					py: 8,
					mx: 4,
					width: "100%",
					margin: "auto",
					background: "linear-gradient(135deg, #2b0a57, #3f0f7d)",
				}}
			>
				{/* Header */}
				<Typography className="trending-quote">Trending Courses</Typography>

				<Typography
					variant="h2"
					className="section-headings"
					color="white"
					mt={1}
				>
					Trending Courses Across Learning Hubs
				</Typography>

				<Typography className="trending-courses-tag">
					Courses that learners are actively enrolling in across Learning Hubs.
				</Typography>

				{/* Carousel */}
				<Box position="relative" className="all-cards-box">
					{/* Navigation */}
					<IconButton
						onClick={handlePrev}
						sx={{
							position: "absolute",
							right: 60,
							top: -60,
							backgroundColor: "#8a2be2",
							color: "white",
							"&:hover": { backgroundColor: "#7a1fd6" },
						}}
					>
						<ArrowBackIosNewIcon fontSize="small" />
					</IconButton>

					<IconButton
						onClick={handleNext}
						sx={{
							position: "absolute",
							right: 0,
							top: -60,
							backgroundColor: "#8a2be2",
							color: "white",
							"&:hover": { backgroundColor: "#7a1fd6" },
						}}
					>
						<ArrowForwardIosIcon fontSize="small" />
					</IconButton>

					{/* Cards */}
					<Box
						ref={scrollRef}
						display="flex"
						gap={2}
						overflow="auto"
						alignItems="stretch"
						onScroll={updateIndexFromScroll}
						sx={{
							scrollBehavior: "smooth",
							"&::-webkit-scrollbar": { display: "none" },
						}}
					>
						{trendingCourses.map((item, index) => (
								<Box
									ref={(el) => (cardRefs.current[index] = el)}   // measure THIS
									sx={{ width: "100%" }}
									display="flex" gap={3} flexWrap="wrap"
								>
									<CourseCard
										course={item}
										courseImg={getRandomImageForCourse(item.id)}
									/>
								</Box>
						))}
					</Box>
				</Box>
			</Box>
		</>
	);
}

export default MasterSkillsSection;
